/**
 * Config & Pricing API Service Module
 *
 * Handles platform configuration, pricing information, and system settings.
 * Most endpoints are public (no authentication required).
 *
 * @module api/config
 * @example
 * ```typescript
 * import { configAPI } from '@/api';
 *
 * // Get EP pricing tiers
 * const { rows: tiers } = await configAPI.getEpTiers({
 *   page: 1,
 *   limit: 20
 * });
 *
 * // Get platform config
 * const config = await configAPI.getConfig();
 * console.log(config.otc_fee_pct);
 * ```
 */

import { api } from './client';
import { isUoBrandedAnnouncement } from '../utils/announcement-display';
import type {
  EpTier,
  PlatformConfig,
  EpPricingResponse,
  UocConfigResponse,
  PaginationParams,
} from './types';

export interface PlatformAnnouncement {
  id: number;
  title: string;
  body?: string;
  create_time: string;
}

/** Normalize GET /api/config/announcements (array, `{ rows }`, or `{ data }`). Hides legacy UO-branded notices. */
export function normalizeAnnouncements(raw: unknown): PlatformAnnouncement[] {
  let rows: PlatformAnnouncement[] = [];
  if (Array.isArray(raw)) rows = raw as PlatformAnnouncement[];
  else if (raw && typeof raw === 'object') {
    const obj = raw as { rows?: unknown; data?: unknown };
    if (Array.isArray(obj.rows)) rows = obj.rows as PlatformAnnouncement[];
    else if (Array.isArray(obj.data)) rows = obj.data as PlatformAnnouncement[];
  }
  return rows.filter((row) => !isUoBrandedAnnouncement(row));
}

/**
 * Config & Pricing API endpoints
 */
export const configAPI = {
  /**
   * GET /api/ep-pricing/tiers
   *
   * Fetch paginated list of EP (Experience Points) pricing tiers.
   * Each tier shows the UO exchange rate for EP in that range.
   *
   * @param params - Pagination parameters
   * @param params.page - Page number (1-indexed)
   * @param params.limit - Items per page
   * @returns Paginated list of EP pricing tiers
   *
   * @example
   * ```typescript
   * const pricing = await configAPI.getEpTiers({
   *   page: 1,
   *   limit: 20
   * });
   *
   * // Returns:
   * // {
   * //   total: 50,
   * //   rows: [
   * //     {
   * //       tier: 1,
   * //       description: "Tier 1: 0-100 EP",
   * //       min_ep: "0",
   * //       max_ep: "100",
   * //       exchange_rate: "1.00"  // 1 EP = 1 UO
   * //     },
   * //     {
   * //       tier: 2,
   * //       description: "Tier 2: 101-500 EP",
   * //       min_ep: "101",
   * //       max_ep: "500",
   * //       exchange_rate: "1.05"  // 1 EP = 1.05 UO
   * //     }
   * //   ]
   * // }
   * ```
   *
   * @required_headers
   * - None (public endpoint)
   *
   * @throws ApiError on server error
   */
  getEpTiers: async (params?: PaginationParams): Promise<EpPricingResponse> =>
    api.get<EpPricingResponse>('/api/ep-pricing/tiers', params),

  /**
   * GET /api/uoc/configs
   *
   * Fetch paginated list of UOC (Unit of Currency) configuration items.
   * Configuration items define system-wide settings and parameters.
   *
   * @param params - Pagination parameters
   * @param params.page - Page number (1-indexed)
   * @param params.limit - Items per page
   * @returns Paginated list of UOC configuration items
   *
   * @example
   * ```typescript
   * const configs = await configAPI.getUocConfigs({
   *   page: 1,
   *   limit: 10
   * });
   *
   * // Returns:
   * // {
   * //   total: 5,
   * //   rows: [
   * //     {
   * //       key: "min_transfer",
   * //       value: "100",
   * //       description: "Minimum transfer amount"
   * //     },
   * //     {
   * //       key: "max_transfer",
   * //       value: "1000000",
   * //       description: "Maximum transfer amount per transaction"
   * //     }
   * //   ]
   * // }
   * ```
   *
   * @required_headers
   * - None (public endpoint)
   *
   * @throws ApiError on server error
   */
  getUocConfigs: async (params?: PaginationParams): Promise<UocConfigResponse> =>
    api.get<UocConfigResponse>('/api/uoc/configs', params),

  /**
   * GET /api/config
   *
   * Fetch platform-wide configuration settings.
   * Returns key configuration values used by the frontend.
   *
   * @returns Platform configuration object
   *
   * @example
   * ```typescript
   * const config = await configAPI.getConfig();
   *
   * // Returns:
   * // {
   * //   otc_fee_pct: "0.5",      // OTC trading fee percentage
   * //   ep_exchange_rate: "1.05", // EP to UO exchange rate
   * //   min_deposit: "100",
   * //   max_withdraw: "1000000",
   * //   system_status: "active"
   * // }
   * ```
   *
   * @required_headers
   * - None (public endpoint)
   *
   * @throws ApiError on server error
   */
  getConfig: async (): Promise<PlatformConfig> =>
    api.get<PlatformConfig>('/api/config/public'),

  /**
   * GET /api/config/public
   *
   * Fetch public configuration settings.
   * Returns configuration accessible to unauthenticated users.
   *
   * @returns Public configuration object
   * @throws ApiError on server error
   */
  getPublicConfig: async (): Promise<PlatformConfig> =>
    api.get<PlatformConfig>('/api/config/public'),

  /**
   * GET /api/config/announcements
   *
   * Fetch system announcements and notifications.
   *
   * @returns Announcements list
   * @throws ApiError on server error
   */
  getAnnouncements: async (): Promise<
    PlatformAnnouncement[] | { rows: PlatformAnnouncement[] }
  > => api.get('/api/config/announcements'),

  /**
   * GET /api/ep-pricing/active
   *
   * Fetch currently active EP pricing tier.
   *
   * @returns Active EP pricing tier
   * @throws ApiError on server error
   */
  getActiveEpPricing: async (params?: { exchange_rate?: string }): Promise<EpTier> =>
    api.get<EpTier>('/api/ep-pricing/active', params),

  /** GET /api/config/unit-price/series — live EP/ORB unit price chart data. */
  getUnitPriceSeries: async (params?: { days?: number }) =>
    api.get<{
      current: string;
      change_pct?: string;
      series: Array<{ t: string; price: number }>;
    }>('/api/config/unit-price/series', params),
};
